import { ElementType, ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { AppIds, AppMenu } from '@cccom/config/app-type';
import { useSettings } from '@cccom/shared/settings';
import { useCompanyLogoSrc } from '@cccom/shared/utils/common';
import { ProfileMenu } from '@cccom/ui/src/components/custom/ProfileMenu';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '../../shadcn/sidebar';
import { AppSwitcher } from '../AppSwitcher';
import { HelpCard } from '../HelpCard';
import { Settings } from '../Settings';
import { AppLogoProps } from './AppLogo';

type MenuItem<TApp extends AppIds> = {
  path: AppMenu[TApp];
  label: string;
  icon?: ElementType;
  isActive?: boolean;
  isCollapsed?: boolean;
};

export type SidebarProps<TApp extends AppIds> = {
  menuItems: MenuItem<TApp>[];
  logoProps: AppLogoProps;
  profileMenuChildren?: ReactNode;
  profileMenuItems?: ReactNode[];
  showSettings?: boolean;
};

export function CCSidebar({
  menuItems,
  logoProps,
  profileMenuChildren,
  profileMenuItems,
  showSettings,
}: SidebarProps<AppIds>) {
  const { pathname } = useLocation();
  const { appList } = useSettings();
  const { state } = useSidebar();
  const { companyLogoSrc } = useCompanyLogoSrc();

  return (
    <Sidebar className="group-data-[side=left]:border-r-0">
      <SidebarHeader className="relative p-0">
        <div className="border-t-primary flex h-[--topbar-height] flex-row items-center gap-3 border-b border-t-4 px-4">
          <AppSwitcher appList={appList} appLogo={logoProps} />
        </div>

        <div className="absolute right-0 top-4 h-8 border-r" />
      </SidebarHeader>

      <SidebarContent className="border-r">
        <SidebarMenu className="gap-4 px-4 py-7">
          {menuItems.map((item) => (
            <SidebarMenuItem key={item.label}>
              <SidebarMenuButton
                asChild
                size="lg"
                variant="rounded"
                isActive={pathname.startsWith(`/${item.path}`)}
              >
                <Link to={item.path!}>
                  {item.icon && <item.icon />}
                  <span className="group-data-[collapsible=offcanvas]:absolute group-data-[collapsible=offcanvas]:opacity-0">
                    {item.label}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="gap-0 border-r p-0">
        {showSettings && <Settings isCollapsed={state === 'collapsed'} />}

        <HelpCard isCollapsed={state === 'collapsed'} />

        <SidebarSeparator className="m-0" />

        <ProfileMenu
          isCollapsed={state === 'collapsed'}
          menuItems={profileMenuItems}
        >
          {profileMenuChildren}
        </ProfileMenu>

        <SidebarSeparator className="m-0" />

        {companyLogoSrc && (
          <div className="p-7 py-4">
            <img alt="Company logo" className="h-7" src={companyLogoSrc} />
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
